import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="font-heading font-bold text-2xl text-foreground-900 mb-2">文章不存在</h2>
          <p className="text-foreground-600 mb-4">该文章可能已被删除或链接失效</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const publishedDate = new Date(article.publishedAt).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '911黑料网',
    },
    image: article.imageUrl,
    url: `https://www.911heilian.com/article/${article.id}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.911heilian.com/article/${article.id}`,
    },
    keywords: article.tags.join(','),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <main className="pt-20 pb-12">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded whitespace-nowrap"
              >
                {article.category}
              </Link>
              {article.isExclusive && (
                <span className="px-2.5 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded whitespace-nowrap">
                  独家爆料
                </span>
              )}
              {article.isHot && (
                <span className="px-2.5 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded whitespace-nowrap">
                  热门
                </span>
              )}
            </div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 leading-snug mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {publishedDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
              </span>
            </div>
          </header>

          <div className="relative w-full h-[300px] md:h-[420px] rounded-lg overflow-hidden mb-8">
            <img
              src={article.imageUrl}
              alt={`911黑料网爆料 - ${article.title}`}
              title={`911黑料网独家爆料 - ${article.title}`}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none mb-8 article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mb-10 pt-6 border-t border-background-200/70">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-background-100 text-foreground-700 text-sm rounded-full whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-between py-4 border-t border-b border-background-200/70 mb-10">
            <div className="flex items-center gap-4">
              <button className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-thumb-up-line"></i> 点赞
              </button>
              <button className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-share-forward-line"></i> 分享
              </button>
              <button className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-bookmark-line"></i> 收藏
              </button>
            </div>
            <button className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-alert-line"></i> 举报
            </button>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-xl text-foreground-900 mb-5">
              相关爆料
            </h2>
            <div className="divide-y divide-background-200/70">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.id}`}
                  className="group flex gap-4 py-4 first:pt-0"
                >
                  <div className="relative w-28 h-20 shrink-0 rounded-md overflow-hidden">
                    <img
                      src={ra.imageUrl}
                      alt={`911黑料网爆料 - ${ra.title}`}
                      title={`911黑料网独家爆料 - ${ra.title}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                      {ra.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-1">{ra.excerpt}</p>
                    <div className="flex items-center gap-2 text-xs text-foreground-500 mt-1 whitespace-nowrap">
                      <span>{ra.author}</span>
                      <span>·</span>
                      <span>{ra.views >= 10000 ? `${(ra.views / 10000).toFixed(1)}万阅读` : `${ra.views}阅读`}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}